import type { BillingException, Priority, ProjectStatus, StatusCategory } from '../lib/types';
import { PROJECT_STATUS_LABELS } from '../lib/types';

const PRIORITY_CLASS: Record<Priority, string> = {
  critical: 'badge badge-accent',
  high: 'badge badge-warn',
  medium: 'badge badge-info',
  low: 'badge',
  none: 'badge',
};

export function PriorityBadge({ priority }: { priority: Priority }) {
  if (priority === 'none') return <span className="dim">—</span>;
  return <span className={PRIORITY_CLASS[priority]}>{priority}</span>;
}

const PROJECT_STATUS_CLASS: Record<ProjectStatus, string> = {
  discovery: 'badge badge-info',
  active: 'badge badge-ok',
  on_hold: 'badge badge-warn',
  delivered: 'badge',
  archived: 'badge',
};

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  return <span className={PROJECT_STATUS_CLASS[status]}>{PROJECT_STATUS_LABELS[status]}</span>;
}

/**
 * A task charged unlike its project, on its row — so a warranty bug on a billable project, or a
 * chargeable one on a free project, is seen before the hours are, not found on the invoice.
 * Nothing at all when the task simply follows its project.
 */
export function BillingBadge({ exception }: { exception?: BillingException }) {
  if (!exception) return null;
  return exception === 'not_billable' ? (
    <span className="badge badge-warn" title="Hours on this task are not charged, though its project is billable">
      not billable
    </span>
  ) : (
    <span className="badge badge-ok" title="Hours on this task are charged, though its project is not billable">
      billable
    </span>
  );
}

export function KindBadge({ kind }: { kind: 'internal' | 'external' }) {
  return (
    <span className={kind === 'internal' ? 'badge badge-accent' : 'badge'}>
      {kind === 'internal' ? 'Egen group' : 'Customer'}
    </span>
  );
}

export function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="row" style={{ gap: 7 }}>
      <span className="dot" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

export const CATEGORY_LABELS: Record<StatusCategory, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  active: 'In progress',
  blocked: 'Blocked',
  review: 'In review',
  done: 'Done',
};
